import { UIMetadataComplete } from '../../../types/UIMetadata';

/**
 * GMC Acadia (linkId 2377) — Denali and AT4 trims.
 *
 * UI half of the 2.0 metadata pair — every key mirrors a configurationId in
 * ./graphics-metadata.ts. Converted from the 1.0 repo's
 * `src/metadata/data/metadata-2377.ts`; shared UI boilerplate
 * (staticTranslations / dealers / saveFormConfig / externalScript) follows
 * 2378. `styleConfig` reuses the GMC palette + `Stratum GMC` face verbatim
 * from 2385 (Canyon) / 2382 (Hummer).
 *
 * Conversion notes:
 *  - Denali and AT4 share ZERO configurationIds — every entity is already
 *    distinct per trim in 1.0, so nothing consolidates; see the
 *    graphics-metadata header for the `hotspots` exception.
 *  - 1.0 `brandItemCode` becomes `autoIntelRPOCode` (variants, colours,
 *    accessories, packages) — key omitted where 1.0 leaves it blank.
 *  - 1.0 `removeAccessories` becomes graphics `groupId` + `isDefault`, derived
 *    per trim: Denali's alloy-wheel pair (20"/22") and cargo-organizer/mat pair,
 *    AT4's Luxury Package pair, and each trim's default-roof/sunroof pair.
 *  - 1.0 `interactionApplyMessage` becomes `affectedInteractionOnApply`; most
 *    cargo accessories snap the tailgate open, Assist Steps snaps the doors
 *    shut. Resolved per-trim since interaction ids don't collide either.
 *  - Packages carry `accessoryIds` mapped from 1.0's numeric
 *    `package.accessoryIDs`.
 *  - 1.0's `engine`/`transmission` filterOptions (Petrol/Turbo Petrol,
 *    CVT/MT/AMT) are generic boilerplate unrelated to Denali/AT4 — dropped,
 *    same call as Hummer/Canyon.
 *  - `toggleTabs` hides CONFIG (no real filters) — 2384 precedent.
 *
 * Assets live in public/assets/{images,videos}/2377/, mirroring the 1.0
 * Denali_* / AT4_* folder layout, resolved by `assetSrc()` in
 * src/utils/metadata.ts.
 */
// Cast through unknown to allow additional model-level UI fields (e.g., name, specifications)
export const uimetadata = {
  models: {
    Acadia: {
      variants: {
        denali: {
          // Year sent to Salesforce in state_info.variant ("YYYY <name>"); API-only.
          leadModelYear: '2026',
          configurationId: 'mlfv94crqnmb',
          name: {
            en: 'Denali',
            ar: 'دينالي',
          },
          description: {
            en: 'AWD',
            ar: 'الدفع الرباعي',
          },
          drivetrain: 'AWD',
          trim: 'Denali',
          highlights: [
            {
              en: '328 HP',
              ar: '328 حصاناً',
            },
            {
              en: 'Torque 442 NM',
              ar: 'عزم الدوران 442 نيوتن متر',
            },
            {
              en: 'Turbo Engine 2.5L',
              ar: 'محرك توربو 2.5 لتر',
            },
          ],
          specs: [
            {
              en: '328 HP',
              ar: '328 حصاناً',
            },
            {
              en: '442 NM',
              ar: '442 نيوتن متر',
            },
          ],
          image: 'images/2377/Variant/DENALI_VARIANT.png',
          price: {
            KSA: 208696,
            UAE: 204800,
            OMR: 21704,
            BHD: 21148,
            QAR: 202574,
            JOD: 39513,
            KW: 17252,
            LBP: 55652,
            IQD: 55652,
            default: 204800,
          },
          basePrice: {
            UAE: 204800,
            KSA: 212000,
            KW: 16300,
            LBP: 72237,
            JOD: 66200,
            BHD: 21032,
            OMR: 20200,
            IQD: 73026522,
            QAR: 204694,
            default: 204800,
          },
          index: 0,
          isDefault: true,
          ar: {
            usdz: '',
            enable: false,
          },
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '5SB',
          brandItemCode: 'GCAC04',
        },
        at4: {
          leadModelYear: '2026',
          configurationId: 'csozs5qjl9ri',
          name: {
            en: 'AT4',
            ar: 'AT4',
          },
          description: {
            en: 'AWD',
            ar: 'الدفع الرباعي',
          },
          drivetrain: 'AWD',
          trim: 'AT4',
          highlights: [
            {
              en: '328 HP',
              ar: '328 حصاناً',
            },
            {
              en: 'Torque 442 NM',
              ar: 'عزم الدوران 442 نيوتن متر',
            },
            {
              en: 'Turbo Engine 2.5L',
              ar: 'محرك توربو 2.5 لتر',
            },
          ],
          specs: [
            {
              en: '328 HP',
              ar: '328 حصاناً',
            },
            {
              en: '442 NM',
              ar: '442 نيوتن متر',
            },
          ],
          image: 'images/2377/Variant/AT4_VARIANT.png',
          price: {
            KSA: 194429,
            UAE: 190800,
            OMR: 20221,
            BHD: 19702,
            QAR: 188726,
            JOD: 36812,
            KW: 16073,
            LBP: 51848,
            IQD: 51848,
            default: 190800,
          },
          basePrice: {
            UAE: 190800,
            KSA: 195300,
            KW: 15200,
            JOD: 61600,
            LBP: 67072,
            OMR: 18800,
            BHD: 19594,
            IQD: 68034474,
            QAR: 190701,
            default: 190800,
          },
          index: 1,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '4SB',
          brandItemCode: 'GCAC03',
        },
      },
      backgrounds: {
        g0ZA968_RpUd: {
          image: 'images/2377/CitySample_2_ENV_day_D.png',
          title: {
            en: 'City Landscape 1',
            ar: 'إطلالة على المدينة 1',
          },
        },
        g0ZA968_RpUe: {
          image: 'images/2377/glass buildings_2.png',
          title: {
            en: 'City Landscape 2',
            ar: 'إطلالة على المدينة 2',
          },
        },
      },
      ambientLighting: {},
      colors: {
        exterior: {
          '7eez7nplog7a': {
            label: {
              en: 'Ebony Twilight Metallic',
              ar: 'إيبوني تويلايت معدني',
            },
            image: 'images/2377/Denali_colors/Ebony_Twilight_Metallic.png',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GB8',
            brandItemCode: 'EX72',
          },
          ruym5z4zk7bp: {
            label: {
              en: 'Sterling Metallic',
              ar: 'فضي معدني',
            },
            image: 'images/2377/Denali_colors/Sterling_Metallic.png',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX20',
          },
          dtjmzz3bsfrl: {
            label: {
              en: 'Glacier White Tricoat',
              ar: 'أبيض جليدي بثلاث طبقات',
            },
            image: 'images/2377/Denali_colors/Glacier_White_Tricoat.png',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G4J',
            brandItemCode: 'EX76',
          },
          svfkt34o1vbs: {
            label: {
              en: 'Downpour Metallic',
              ar: 'مطري معدني',
            },
            image: 'images/2377/Denali_colors/Downpour_Metallic.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GXP',
            brandItemCode: 'EX21',
          },
          '2h6rvjratd39': {
            label: {
              en: 'Volcanic Red Tintcoat',
              ar: 'أحمر بركاني بطبقتين',
            },
            image: 'images/2377/Denali_colors/Volcanic_Red_Tintcoat.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX19',
          },
          ys6fc5ecm0gr: {
            label: {
              en: 'Summit White',
              ar: 'أبيض',
            },
            image: 'images/2377/Denali_colors/Summit_White.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          '7eez7neplog7a': {
            label: {
              en: 'Ebony Twilight Metallic',
              ar: 'إيبوني تويلايت معدني',
            },
            image: 'images/2377/AT4_colors/Ebony_Twilight_Metallic.png',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GB8',
            brandItemCode: 'EX72',
          },
          ruym5ze4zk7bp: {
            label: {
              en: 'Sterling Metallic',
              ar: 'فضي معدني',
            },
            image: 'images/2377/AT4_colors/Sterling_Metallic.png',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX20',
          },
          dtjmezz3bsfrl: {
            label: {
              en: 'Glacier White Tricoat',
              ar: 'أبيض جليدي بثلاث طبقات',
            },
            image: 'images/2377/AT4_colors/Glacier_White_Tricoat.png',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G4J',
            brandItemCode: 'EX76',
          },
          htk1s9thjb4s: {
            label: {
              en: 'Riverstone Metallic',
              ar: 'ريفرستون معدني',
            },
            image: 'images/2377/AT4_colors/Riverstone_Metallic.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GFR',
            brandItemCode: 'EX88',
          },
          '2h6rvejratd39': {
            label: {
              en: 'Volcanic Red Tintcoat',
              ar: 'أحمر بركاني بطبقتين',
            },
            image: 'images/2377/AT4_colors/Volcanic_Red_Tintcoat.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX19',
          },
          ys6fcee5ecm0gr: {
            label: {
              en: 'Summit White',
              ar: 'أبيض',
            },
            image: 'images/2377/AT4_colors/Summit_White.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
        },
        interior: {
          '5vktfxuscn42': {
            label: {
              en: 'Sheer/ Very Dark Atmosphere with Perforated leather Appointed Seats',
              ar: 'لون شفاف / بيج داكن جداً مع مقاعد جلدية مثقبة',
            },
            image: 'images/2377/Denali_colors/Sheer_Dark_Atmosphere_Perforated_Leather.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'H6Z',
            brandItemCode: 'IN75',
          },
          xyus3yrafri2: {
            label: {
              en: 'After dark with Perforated Leather Appointed Seats',
              ar: 'لون After dark مع مقاعد جلدية مثقبة',
            },
            image: 'images/2377/Denali_colors/After_Dark_Perforated_Leather.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 1,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'H6Y',
            brandItemCode: 'IN34',
          },
          hp43ibg9l4ft: {
            label: {
              en: 'Forest Storm w/Mahogany Accents, CoreTec with Cloth Inserts seat trim',
              ar: 'مقعد بلون زيتي داكن مع لمسات من خشب الماهوغني، وبطانة من CoreTec مع حشوات من القماش',
            },
            image: 'images/2377/AT4_colors/Forest_Storm_w_Mohagany_Accents.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 2,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'H6V',
            brandItemCode: 'IN44',
          },
        },
      },
      features: {
        av8ukajwzjsm: {
          label: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية',
          },
          image: 'images/2377/Denali_int/20_inch_Alloy.jpg',
          title: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية',
          },
          desc: {
            en: '20" Pearl Nickel Machined Aluminium ',
            ar: 'عجلات ألومنيوم مشغول آلياً بلون النيكل اللؤلؤي مقاس 20 إنش',
          },
          type: 'exterior',
        },
        '56b99wbs39ki': {
          label: {
            en: 'Denali Signature Grille',
            ar: 'شبك أمامي خاص بطراز دينالي',
          },
          image: 'images/2377/Denali_int/Denali_Grille.jpg',
          title: {
            en: 'Denali Signature Grille',
            ar: 'شبك أمامي خاص بطراز دينالي',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        slfnh3hjd6s8: {
          label: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED',
          },
          image: 'images/2377/Denali_int/Headlamps.jpg',
          title: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        e31dagcaee09: {
          label: {
            en: 'LED Taillamps',
            ar: 'مصابيح خلفية LED',
          },
          image: 'images/2377/Denali_int/Taillamp.jpg',
          title: {
            en: 'LED Taillamps',
            ar: 'مصابيح خلفية LED',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        e31dagcaeee09: {
          label: {
            en: 'ORVM',
            ar: 'المرايا الجانبية الخارجية ORVM',
          },
          image: 'images/2377/Denali_int/ORVM.jpg',
          title: {
            en: 'ORVM',
            ar: 'المرايا الجانبية الخارجية ORVM',
          },
          desc: {
            en: 'Outside heated, power adjustable, power folding, body color, driver side auto dimming with integrated turn signal indicators',
            ar: 'مرايا خارجية قابلة للتدفئة، قابلة للتعديل كهربائياً، قابلة للطي كهربائياً، بلون الهيكل، تعتيم تلقائي من جانب السائق مع مؤشرات انعطاف مدمجة',
          },
          type: 'exterior',
        },
        e31dagccaeee09: {
          label: {
            en: 'AutoSense Power Liftgate',
            ar: 'باب خلفي كهربائي AutoSense',
          },
          image: 'images/2377/Denali_int/Power_Liftgate.jpg',
          title: {
            en: 'AutoSense Power Liftgate',
            ar: 'باب خلفي كهربائي AutoSense',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        '3ofodapu2ddp': {
          label: {
            en: 'Head-up Display',
            ar: 'شاشة عرض علوية أمامية',
          },
          image: 'images/2377/Denali_int/HUD.jpg',
          title: {
            en: 'Head-up Display',
            ar: 'شاشة عرض علوية أمامية',
          },
          desc: {
            en: 'Configurable 8" diagonal Head-up display',
            ar: 'شاشة عرض علوية أمامية قطرية قابلة للتشكيل مقاس 8 إنش',
          },
          type: 'interior',
        },
        muey3bbku37k: {
          label: {
            en: 'Seats',
            ar: 'المقاعد',
          },
          image: 'images/2377/Denali_int/seat.jpg',
          title: {
            en: 'Seats',
            ar: 'المقاعد',
          },
          desc: {
            en: 'Heated and ventilated First Row Seats and Heated Second Row Seats',
            ar: 'مقاعد الصف الأول قابلة للتدفئة والتهوية ومقاعد الصف الثاني قابلة للتدفئة',
          },
          type: 'interior',
        },
        z7m52iyopf8c: {
          label: {
            en: 'Infotainment Screen',
            ar: 'شاشة المعلومات والترفيه',
          },
          image: 'images/2377/Denali_int/Info_screen.jpg',
          title: {
            en: 'Infotainment Screen',
            ar: 'شاشة المعلومات والترفيه',
          },
          desc: {
            en: '15" Diagonal GMC infotainment system with navigation',
            ar: 'نظام معلومات وترفيه GMC مقاس 15 إنش مع نظام ملاحة',
          },
          type: 'interior',
        },
        awla0dgc0vgs: {
          label: {
            en: 'Drive Information System',
            ar: 'نظام معلومات السائق',
          },
          image: 'images/2377/Denali_int/dic.jpg',
          title: {
            en: 'Drive Information System',
            ar: 'نظام معلومات السائق',
          },
          desc: {
            en: '11" Diagonal Driver information system',
            ar: 'نظام معلومات السائق مقاس 11 إنش',
          },
          type: 'interior',
        },
        h0dy3khszwvy: {
          label: {
            en: 'Audio System',
            ar: 'النظام الصوتي',
          },
          image: 'images/2377/Denali_int/Speaker_Denali.jpg',
          title: {
            en: 'Audio System',
            ar: 'النظام الصوتي',
          },
          desc: {
            en: 'Bose Premium 12-Speaker Audio System',
            ar: 'نظام Bose الصوتي الممتاز بـ 12 مكبر صوت',
          },
          type: 'interior',
        },
        vsefclj6vozz: {
          label: {
            en: 'Ambient light',
            ar: 'إضاءة محيطة',
          },
          image: 'images/2377/Denali_int/Ambient_Lighting.jpg',
          title: {
            en: 'Ambient light',
            ar: 'إضاءة محيطة',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        '7ncmf61zbg9j': {
          label: {
            en: '360 Camera',
            ar: 'كاميرا 360 درجة',
          },
          image: 'images/2377/Denali_int/360_Camera.jpg',
          title: {
            en: '360 Camera',
            ar: 'كاميرا 360 درجة',
          },
          desc: {
            en: '360 Camera with washer',
            ar: 'كاميرا 360 درجة مع غاسلة',
          },
          type: 'interior',
        },
        jh03x1qu9di0: {
          label: {
            en: 'Rear Camera Mirror',
            ar: 'مرآة الكاميرا الخلفية',
          },
          image: 'images/2377/Denali_int/IRVM.jpg',
          title: {
            en: 'Rear Camera Mirror',
            ar: 'مرآة الكاميرا الخلفية',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        qesaxf0nj162: {
          label: {
            en: 'Second Row One Touch Flat Folding',
            ar: 'الصف الثاني قابل للطي بلمسة واحدة',
          },
          image: 'images/2377/Denali_int/2nd_row.jpg',
          title: {
            en: 'Second Row One Touch Flat Folding',
            ar: 'الصف الثاني قابل للطي بلمسة واحدة',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        qesaxfr0nj162: {
          label: {
            en: '60/40 Split',
            ar: 'تقسيم 60/40',
          },
          image: 'images/2377/Denali_int/3rd_row.jpg',
          title: {
            en: '60/40 Split',
            ar: 'تقسيم 60/40',
          },
          desc: {
            en: 'Third Row 60/40 Power Folding',
            ar: 'الصف الثالث قابل للطي كهربائيًا بنسبة 60/40',
          },
          type: 'interior',
        },
        et1jiatqexbt: {
          label: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية',
          },
          image: 'images/2377/AT4_int/Wheel.jpg',
          title: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية',
          },
          desc: {
            en: '18" Dark Machined Aluminium Wheels With All-Terrain Tires',
            ar: 'عجلات من الألومنيوم الداكن المشغول آلياً مقاس 18 إنش مع إطارات لجميع التضاريس',
          },
          type: 'exterior',
        },
        kh1xk6tsachv: {
          label: {
            en: 'Exterior Design',
            ar: 'التصميم الخارجي',
          },
          image: 'images/2377/AT4_int/Accented_Exterior.jpg',
          title: {
            en: 'Exterior Design',
            ar: 'التصميم الخارجي',
          },
          desc: {
            en: 'AT4 Exclusive Dark Accented Exterior design',
            ar: 'تصميم خارجي حصري بلمسات داكنة خاصة بطراز AT4',
          },
          type: 'exterior',
        },
        n9uci5v0q0no: {
          label: {
            en: 'AT4 Grille',
            ar: 'شبك AT4',
          },
          image: 'images/2377/AT4_int/AT4_Grille.jpg',
          title: {
            en: 'AT4 Grille',
            ar: 'شبك AT4',
          },
          desc: {
            en: 'Dark Night Rider Finish Grille',
            ar: 'شبك أمامي بلمسة نهائية داكنة من Dark Night Rider',
          },
          type: 'exterior',
        },
        '6jn6rb1ajk00': {
          label: {
            en: 'Roof Rails',
            ar: 'سكك السقف',
          },
          image: 'images/2377/AT4_int/roof_rail.jpg',
          title: {
            en: 'Roof Rails',
            ar: 'سكك السقف',
          },
          desc: {
            en: 'Gloss Black Outside Mirrors & Roof Rails',
            ar: 'مرايا خارجية وسكك سقف باللون الأسود اللامع',
          },
          type: 'exterior',
        },
        fzpdk9bjmbo4: {
          label: {
            en: 'Dual Exhaust System',
            ar: 'نظام العادم المزدوج',
          },
          image: 'images/2377/AT4_int/exhaust.jpg',
          title: {
            en: 'Dual Exhaust System',
            ar: 'نظام العادم المزدوج',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        '1m71ax5f4id6': {
          label: {
            en: 'Off-Road Tuned Suspension',
            ar: 'نظام تعليق مُعدّل للطرق الوعرة',
          },
          image: 'images/2377/AT4_int/Off_Road_Suspension.jpg',
          title: {
            en: 'Off-Road Tuned Suspension',
            ar: 'نظام تعليق مُعدّل للطرق الوعرة',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        thexucnyzlqx: {
          label: {
            en: 'Skid Plate & Red Tow Hooks',
            ar: 'لوح حماية سفلي وحلقات سحب حمراء',
          },
          image: 'images/2377/AT4_int/Tow_Hooks.jpg',
          title: {
            en: 'Skid Plate & Red Tow Hooks',
            ar: 'لوح حماية سفلي وحلقات سحب حمراء',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        fz9wua33l4wm: {
          label: {
            en: 'Infotainment Screen',
            ar: 'شاشة المعلومات والترفيه',
          },
          image: 'images/2377/AT4_int/info_screen.jpg',
          title: {
            en: 'Infotainment Screen',
            ar: 'شاشة المعلومات والترفيه',
          },
          desc: {
            en: '15" Diagonal GMC infotainment system',
            ar: 'نظام المعلومات والترفيه GMC مقاس 15 إنش',
          },
          type: 'interior',
        },
        a5fob6prph1z: {
          label: {
            en: 'Audio System',
            ar: 'النظام الصوتي',
          },
          image: 'images/2377/AT4_int/speaker.jpg',
          title: {
            en: 'Audio System',
            ar: 'النظام الصوتي',
          },
          desc: {
            en: 'Bose Premium 12-Speaker Audio System',
            ar: 'نظام Bose الصوتي الممتاز بـ 12 مكبر صوت',
          },
          type: 'interior',
        },
        pro1ugk226q0: {
          label: {
            en: 'Interior Design',
            ar: 'التصميم الداخلي',
          },
          image: 'images/2377/AT4_int/Accented_Interior.jpg',
          title: {
            en: 'Interior Design',
            ar: 'التصميم الداخلي',
          },
          desc: {
            en: 'AT4 Exclusive Dark Accented Interior design',
            ar: 'تصميم داخلي حصري بلمسات داكنة خاصة بطراز AT4',
          },
          type: 'interior',
        },
        sp46ano5o1g6: {
          label: {
            en: 'Driver 8-way power seat adjuster',
            ar: 'مقعد السائق قابل للتعديل كهربائياً في 8 وضعيات',
          },
          image: 'images/2377/AT4_int/seats.jpg',
          title: {
            en: 'Driver 8-way power seat adjuster',
            ar: 'مقعد السائق قابل للتعديل كهربائياً في 8 وضعيات',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        po1vxwjvhsua: {
          label: {
            en: 'Passenger 6-way power seat adjuster',
            ar: 'مقعد الراكب قابل للتعديل كهربائياً في 6 وضعيات',
          },
          image: 'images/2377/AT4_int/seats.jpg',
          title: {
            en: 'Passenger 6-way power seat adjuster',
            ar: 'مقعد الراكب قابل للتعديل كهربائياً في 6 وضعيات',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        '10giu7b6ijpj': {
          label: {
            en: 'Drive Information System',
            ar: 'نظام معلومات السائق',
          },
          image: 'images/2377/AT4_int/DIC.jpg',
          title: {
            en: 'Drive Information System',
            ar: 'نظام معلومات السائق',
          },
          desc: {
            en: '11" Diagonal Driver information system',
            ar: 'نظام معلومات السائق مقاس 11 إنش',
          },
          type: 'interior',
        },
        '10giu7fb6ijpj': {
          label: {
            en: 'Selectable Drive Mode',
            ar: 'وضع قيادة قابل للاختيار',
          },
          image: 'images/2377/AT4_int/Selectable_Drive_Mode.jpg',
          title: {
            en: 'Selectable Drive Mode',
            ar: 'وضع قيادة قابل للاختيار',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        '10giue7fb6ijpj': {
          label: {
            en: 'Heated Front Seats',
            ar: 'مقاعد أمامية قابلة للتدفئة',
          },
          image: 'images/2377/AT4_int/seats.jpg',
          title: {
            en: 'Heated Front Seats',
            ar: 'مقاعد أمامية قابلة للتدفئة',
          },
          desc: {
            en: 'Heated Driver & front Passenger Seats',
            ar: 'مقاعد قابلة للتدفئة للسائق والراكب الأمامي',
          },
          type: 'interior',
        },
      },
      accessories: {
        '3w06jsi5oinw': {
          label: {
            en: '20" Pearl_Nickel_Machined_Aluminium',
            ar: '',
          },
          image: '',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        whv4p5vjna3p: {
          label: {
            en: '22" Pearl Nickel Machined Aluminum Wheels',
            ar: 'عجلات من الألومنيوم المشغول آلياً بلون النيكل اللؤلؤي مقاس 22 إنش',
          },
          image: 'images/2377/Denali_acc/22_inch Wheel_optional.jpg',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RUJ',
          brandItemCode: 'AC136',
        },
        dpsosp5gxrx3: {
          label: {
            en: 'Vertical Cargo Net with Storage Bag',
            ar: 'شبكة أمتعة عمودية مع حقيبة تخزين',
          },
          image: 'images/2377/Denali_acc/Vertical_Cargo_Net.png',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'W2D',
          brandItemCode: 'AC233',
        },
        prnt5nsa6jxn: {
          label: {
            en: 'Cargo Organizer in Jet Black with GMC Logo',
            ar: 'منظم أمتعة باللون الأسود الداكن مع شعار GMC',
          },
          image: 'images/2377/Denali_acc/Cargo_Organiser.png',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC168',
        },
        '9kq6x8627fks': {
          label: {
            en: 'Premium Carpeted Cargo Area Mat in Jet Black with Integrated Cargo Bins',
            ar: 'سجادة أرضية فاخرة لمنطقة الأمتعة باللون الأسود الداكن مع صناديق تخزين مدمجة',
          },
          image: 'images/2377/Denali_acc/Premium_Cargo_Mat_with_Bins.png',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'B5C',
          excludeFromRecommendation: true,
          brandItemCode: 'AC203',
        },
        isd8oqmgpfud: {
          label: {
            en: 'Integrated Cargo Liner in Jet Black with GMC Logo',
            ar: 'بطانة تحميل مدمجة بلون أسود داكن مع شعار GMC',
          },
          image: 'images/2377/Denali_acc/Integrated_Cargo_Liner.png',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'CAV',
          brandItemCode: 'AC194',
        },
        d0b6pb4rgdcw: {
          label: {
            en: 'Cargo Shelf with Dividers',
            ar: 'رف تحميل مع فواصل',
          },
          image: 'images/2377/Denali_acc/Cargo_Shelf_with_Dividers.png',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC169',
        },
        oe069xyvrqoq: {
          label: {
            en: 'First-Row Premium All-Weather Floor Liners in Jet Black with GMC Logo',
            ar: 'بطانات أرضية فاخرة للصف الأول لجميع الأحوال الجوية باللون الأسود الداكن مع شعار GMC',
          },
          image: 'images/2377/Denali_acc/First_Row_Floor_Liners.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RIA',
          brandItemCode: 'AC175',
        },
        h3ahwov6h753: {
          label: {
            en: 'Second-Row Interlocking Premium All-Weather Floor Liners in Jet Black',
            ar: 'بطانات أرضية فاخرة متشابكة للصف الثاني لجميع الأحوال الجوية باللون الأسود الداكن',
          },
          image: 'images/2377/Denali_acc/Second_Row_Floor_Liners.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC211',
        },
        an0ysv4lgrpq: {
          label: {
            en: 'Third-Row One-Piece Premium All-Weather Floor Liner in Jet Black',
            ar: 'بطانة أرضية فاخرة من قطعة واحدة للصف الثالث لجميع الأحوال الجوية باللون الأسود الداكن',
          },
          image: 'images/2377/Denali_acc/Camera_Third_Row_Floor_Liners_DENALI.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RIB',
          brandItemCode: 'AC225',
        },
        ssenv85hajoq: {
          label: {
            en: 'Assist Steps',
            ar: 'عتبات مساعدة',
          },
          image: 'images/2377/Denali_acc/Assist _Steps_Shark_Skin_Metallic.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC56',
        },
        izsin73px8pj: {
          label: {
            en: 'Variant_Parts_Denali',
            ar: 'قطع غيار دينالي متنوعة',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        ogh3vezi7oc0: {
          label: {
            en: 'Std_Roof_Sheer_Dark_Atmosphere',
            ar: 'لون شفاف / بيج داكن قياسي للسقف',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        gzamkqryq6wb: {
          label: {
            en: 'Panoramic Sunroof',
            ar: 'فتحة سقف بانورامية',
          },
          image: 'images/2377/Denali_acc/Denali_SunRoof.jpg',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'CAJ',
          brandItemCode: 'OP14',
        },
        '62c2lgtcvtgb': {
          label: {
            en: '3rd_Row_Mat_Denali_Default',
            ar: 'فتحة سقف بانورامية',
          },
          image: 'images/2377/Denali_acc/Denali_SunRoof.jpg',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        o0effisb7huz: {
          label: {
            en: 'Vertical Cargo Net with Storage Bag',
            ar: 'شبكة أمتعة عمودية مع حقيبة تخزين',
          },
          image: 'images/2377/AT4_acc/Vertical_Cargo_Net.png',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'W2D',
          brandItemCode: 'AC233',
        },
        arv6xc8di0p2: {
          label: {
            en: 'Cargo Organizer in Jet Black with GMC Logo',
            ar: 'منظم أمتعة باللون الأسود الداكن مع شعار GMC',
          },
          image: 'images/2377/AT4_acc/Cargo_Organiser.png',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC168',
        },
        z60cgrm95zsc: {
          label: {
            en: 'Premium Carpeted Cargo Area Mat in Jet Black with Integrated Cargo Bins',
            ar: 'سجادة أرضية فاخرة لمنطقة الأمتعة باللون الأسود الداكن مع صناديق تخزين مدمجة',
          },
          image: 'images/2377/AT4_acc/Premium_Cargo_Mat_with_Bins.png',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'B5C',
          excludeFromRecommendation: true,
          brandItemCode: 'AC203',
        },
        k4u455dsgte4: {
          label: {
            en: 'Integrated Cargo Liner in Jet Black with GMC Logo',
            ar: 'بطانة تحميل مدمجة بلون أسود داكن مع شعار GMC',
          },
          image: 'images/2377/AT4_acc/Integrated_Cargo_Liner.png',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'CAV',
          brandItemCode: 'AC194',
        },
        xw46c5hhztat: {
          label: {
            en: 'Cargo Shelf with Dividers',
            ar: 'رف تحميل مع فواصل',
          },
          image: 'images/2377/AT4_acc/Cargo_Shelf_with_Dividers.png',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC169',
        },
        '7ua1p3j98kiy': {
          label: {
            en: 'First-Row Premium All-Weather Floor Liners in Jet Black with GMC Logo',
            ar: 'بطانات أرضية فاخرة للصف الأول لجميع الأحوال الجوية باللون الأسود الداكن مع شعار GMC',
          },
          image: 'images/2377/AT4_acc/First_Row_Floor_Liners.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RIA',
          brandItemCode: 'AC175',
        },
        t09zdke9k241: {
          label: {
            en: 'Second-Row Interlocking Premium All-Weather Floor Liners in Jet Black',
            ar: 'بطانات أرضية فاخرة متشابكة للصف الثاني لجميع الأحوال الجوية باللون الأسود الداكن',
          },
          image: 'images/2377/AT4_acc/Second_Row_Floor_Liners.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC211',
        },
        x8m9byz4k9y4: {
          label: {
            en: 'Third-Row One-Piece Premium All-Weather Floor Liner in Jet Black',
            ar: 'بطانة أرضية فاخرة من قطعة واحدة للصف الثالث لجميع الأحوال الجوية باللون الأسود الداكن',
          },
          image: 'images/2377/AT4_acc/Camera_Third_Row_Floor_Liners.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'RIB',
          brandItemCode: 'AC225',
        },
        '8h02oi0oprfb': {
          label: {
            en: 'Assist Steps',
            ar: 'عتبات مساعدة',
          },
          image: 'images/2377/AT4_acc/Assist _Steps_Shark_Skin_Metallic.jpg',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          brandItemCode: 'AC56',
        },
        d3hm2vqzttar: {
          label: {
            en: 'WO_Luxury_package',
            ar: '',
          },
          image: '',
          category: {
            en: 'Package',
            ar: 'الفئة',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '1z85m5ckyrut': {
          label: {
            en: 'W_Luxury_package',
            ar: '',
          },
          image: '',
          category: {
            en: 'Package',
            ar: 'الفئة',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        jackzeoq4kxc: {
          label: {
            en: 'Variant_Parts_AT4',
            ar: '',
          },
          image: '',
          category: {
            en: 'Package',
            ar: 'الفئة',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '2cw4gpca53so': {
          label: {
            en: 'Std_Roof_Forest_Storm',
            ar: 'لون شفاف / بيج داكن قياسي للسقف',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        f85e14irfgjr: {
          label: {
            en: 'Panoramic Sunroof',
            ar: 'فتحة سقف بانورامية',
          },
          image: 'images/2377/AT4_acc/AT4_SunRoof.jpg',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'CAJ',
          brandItemCode: 'OP14',
        },
        '5rtjv1h7i4pw': {
          label: {
            en: '3rd_Row_Mat_Denali_Default',
            ar: 'فتحة سقف بانورامية',
          },
          image: 'images/2377/Denali_acc/Denali_SunRoof.jpg',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
      },
      packages: {
        a1lyjpkbd835: {
          label: {
            en: 'Luxury Package',
            ar: 'فئة الفخامة',
          },
          image: 'images/2377/AT4_Luxury_pac/Acoustic_style_windshield.jpg',
          category: {
            en: 'Packages',
            ar: 'الفئات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          packageDescription: {
            en: 'Luxury Package comes with heated and ventilated, driver and front passenger seat cushions and seat backs, acoustic style windshield, heated wiper park, heated rear outboard seats and 8" Diagonal Head-Up Display',
            ar: 'تتضمن فئة الفخامة وسائد ومساند مقاعد أمامية قابلة للتدفئة والتهوية للسائق والراكب الأمامي، والزجاج الأمامي العازل للصوت، ومنطقة ماسحات زجاج قابلة للتدفئة، ومقاعد خلفية جانبية قابلة للتدفئة، وشاشة عرض علوية أمامية مقاس 8 إنش.',
          },
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Heated and Ventilated Seats',
                ar: 'مقاعد قابلة للتدفئة والتهوية',
              },
              accessoryDescription: {
                en: 'Heated and ventilated, driver and front passenger seat cushions and seat backs',
                ar: 'وسائد ومساند مقاعد السائق والراكب الأمامي قابلة للتدفئة والتهوية',
              },
              image: 'images/2377/AT4_Luxury_pac/seats.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                KW: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'Acoustic style windshield',
                ar: 'زجاج أمامي بعزل صوتي',
              },
              accessoryDescription: {
                en: 'Acoustic style windshield',
                ar: 'زجاج أمامي بعزل صوتي',
              },
              image: 'images/2377/AT4_Luxury_pac/Acoustic_style_windshield.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                KW: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'Heated wiper park',
                ar: 'منطقة ماسحات الزجاج قابلة للتدفئة',
              },
              accessoryDescription: {
                en: 'Heated wiper park',
                ar: 'منطقة ماسحات الزجاج قابلة للتدفئة',
              },
              image: 'images/2377/AT4_Luxury_pac/Heated_WIper_Park.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                KW: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'Heated rear outboard seats',
                ar: 'مقاعد خلفية جانبية مدفأة',
              },
              accessoryDescription: {
                en: 'Heated rear outboard seats',
                ar: 'مقاعد خلفية جانبية مدفأة',
              },
              image: 'images/2377/AT4_Luxury_pac/Heated_rear_outboard_seats.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                KW: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: '8" Diagonal Head-Up Display',
                ar: 'شاشة عرض علوية أمامية مقاس 8 إنش',
              },
              accessoryDescription: {
                en: '8" Diagonal Head-Up Display',
                ar: 'شاشة عرض علوية أمامية مقاس 8 إنش',
              },
              image: 'images/2377/AT4_Luxury_pac/hud.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                KW: 0,
                default: 0,
              },
            },
          ],
          autoIntelRPOCode: 'WPL',
          brandItemCode: 'PA01',
        },
      },
      interactions: {
        nw7ayiomff7o: {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        smjhed8evi5i: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة',
          },
          type: 'interaction',
        },
        '7x0dl7w7ygmd': {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف',
          },
          type: 'interaction',
        },
        z50uy8f03jh7: {
          label: {
            en: 'Headlight',
            ar: 'المصابيح الأمامية',
          },
          type: 'interaction',
        },
        '14eisbljp9pi': {
          label: {
            en: 'DRL',
            ar: 'DRL',
          },
          type: 'interaction',
        },
        '1vqrly7ngbhv': {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي',
          },
          type: 'interaction',
        },
        aq2x1j1r6h63: {
          label: {
            en: 'Seats Folding',
            ar: 'مقاعد قابلة للطي',
          },
          type: 'interaction',
        },
        q4efn17j0xpb: {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        cid63atonu3q: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة',
          },
          type: 'interaction',
        },
        '8y1ygewkzqwp': {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف',
          },
          type: 'interaction',
        },
        xgeya76hww4y: {
          label: {
            en: 'Headlight',
            ar: 'المصابيح الأمامية',
          },
          type: 'interaction',
        },
        z4fmgvbj6vl6: {
          label: {
            en: 'DRL',
            ar: 'DRL',
          },
          type: 'interaction',
        },
        j9phsdy1vqre: {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي',
          },
          type: 'interaction',
        },
        aq2x1j1r6h61: {
          label: {
            en: 'Seats Folding',
            ar: 'مقاعد قابلة للطي',
          },
          type: 'interaction',
        },
      },
      hotspots: {
        exteriorCamera: {
          title: {
            en: 'Exterior',
            ar: 'الخارج',
          },
        },
        interiorCamera: {
          title: {
            en: 'First Row Seat',
            ar: 'مقعد الصف الأول',
          },
        },
        interiorCamera_1: {
          title: {
            en: 'Second Row Seat',
            ar: 'مقعد الصف الثاني',
          },
        },
        interiorCamera_2: {
          title: {
            en: 'Third Row Seat',
            ar: 'مقعد الصف الثالث',
          },
        },
      },
      name: {
        en: 'GMC Acadia',
        ar: 'جي إم سي أكاديا',
      },
      specifications: {
        heading: {
          en: 'GMC Acadia',
          ar: 'جي إم سي أكاديا',
        },
        trim: {
          en: 'Denali',
          ar: 'Denali',
        },
        price: {
          en: 'Starting from 190800 MSRP',
          ar: 'ابتداء من 190800 MSRP',
        },
        model: 'GMC Acadia',
        seatingCapacity: '7',
        dimensions: {
          length: '5181 mm',
          width: '',
          height: '',
          wheelbase: '3072 mm',
          groundClearance: '',
        },
        performance: [
          {
            value: '328',
            unit: 'HP',
            label: {
              en: 'Horsepower',
              ar: 'حصان',
            },
          },
          {
            value: '442',
            unit: 'NM',
            label: {
              en: 'Torque',
              ar: 'عزم الدوران',
            },
          },
          {
            value: '2.5L',
            unit: '',
            label: {
              en: 'Turbo Engine',
              ar: 'محرك توربو',
            },
          },
        ],
        summary: [
          {
            title: {
              en: 'Model',
              ar: 'الطراز',
            },
            subtitle: {
              en: 'GMC Acadia',
              ar: 'GMC أكاديا',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Exterior Colors',
              ar: 'الألوان الخارجية',
            },
            subtitle: {
              en: 'Ebony Twilight Metallic, Summit White, Volcanic Red Tintcoat, Sterling Metallic, Downpour Metallic (Denali trim only), Glacier White Tricoat, Riverstone Metallic (AT4 trim only)',
              ar: 'إيبوني تويلايت معدني، أبيض، أحمر بركاني بطبقتين، فضي معدني، مطري معدني (في طراز دينالي فقط)، أبيض جليدي ثلاثي الطبقات، ريفرستون معدني (في طراز AT4 فقط)',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Interior',
              ar: 'المقصورة الداخلية',
            },
            subtitle: {
              en: 'AT4: Forest Storm w/Mahogany Accents CoreTec with Cloth Inserts seat trim and Denali: Sheer/ Very Dark Atmosphere with Perforated leather Appointed Seats, After dark with Perforated Leather Appointed Seats',
              ar: 'AT4: زيتي داكن مع لمسات من خشب الماهوغني وتطعيمات من القماش ومقاعد من CoreTec\nدينالي: لون شفاف/ بيج داكن جداً مع مقاعد جلدية مثقبة، ولون After dark مع مقاعد جلدية مثقبة',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Wheelbase',
              ar: 'قاعدة العجلات',
            },
            subtitle: {
              en: '3072 mm',
              ar: '3072 ملم',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Length',
              ar: 'الطول',
            },
            subtitle: {
              en: '5181 mm',
              ar: '5181 ملم',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Cargo Space',
              ar: 'حيز الأمتعة',
            },
            subtitle: {
              en: 'Behind 1st row seat- 2758.4 litres, Behind 2nd row seat- 1608.6 litres, Behind 3rd row seat- 648.5 liters',
              ar: 'خلف مقاعد الصف الأول: 2758.4 لتر، خلف مقاعد الصف الثاني: 1608.6 لتر، خلف مقاعد الصف الثالث: 648.5 لتر',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Transmission',
              ar: 'نقل الحركة',
            },
            subtitle: {
              en: '8-speed Automatic',
              ar: 'ناقل حركة أوتوماتيكي بـ 8 سرعات',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'OnStar Basics',
              ar: 'باقة اونستار الأساسية',
            },
            subtitle: {
              en: '8 Years',
              ar: '8 سنوات',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'OnStar One',
              ar: 'باقة اونستار ون',
            },
            subtitle: {
              en: 'Upto 3 Years',
              ar: 'حتى 3 سنوات',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Seating Capacity',
              ar: 'عدد الركاب',
            },
            subtitle: {
              en: '7',
              ar: '7',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Infotainment Screen',
              ar: 'شاشة المعلومات والترفيه',
            },
            subtitle: {
              en: '15" diagonal Premium GMC Infotainment System',
              ar: 'نظام المعلومات والترفيه المتميز من GMC مقاس 15 إنش',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
        ],
      },
      accessoryCategories: {
        exterior: [
          'Roof',
          'Alloy Wheels',
        ],
      },
    },
  },
  plugins: {
    deferredAssetLoading: true,
  },
  staticTranslations: {
    specDetails: {
      model: {
        en: 'Model',
        ar: 'نموذج',
      },
      exteriorColours: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interiorAmbience: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      seatingCapacity: {
        en: 'Seating Capacity',
        ar: 'سعة المقاعد',
      },
      length: {
        en: 'Length',
        ar: 'الطول',
      },
      width: {
        en: 'Width',
        ar: 'العرض',
      },
      height: {
        en: 'Height',
        ar: 'الارتفاع',
      },
      wheelbase: {
        en: 'Wheelbase',
        ar: 'قاعدة العجلات',
      },
      groundClearance: {
        en: 'Ground Clearance',
        ar: 'الخلوص الأرضي',
      },
      share: {
        en: 'Share',
        ar: 'يشارك',
      },
    },
    variantSelection: {
      title: {
        en: 'Model',
        ar: 'نموذج',
      },
      select: {
        en: 'Select',
        ar: 'تحديد',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      selectBodyStyle: {
        en: 'Select Body Style',
        ar: 'اختر نمط الهيكل',
      },
      availableTrims: {
        en: 'Select Trim',
        ar: 'اختر الفئة',
      },
      driveType: {
        en: 'Drive Type',
        ar: 'نوع القيادة',
      },
      model: {
        en: 'Model',
        ar: 'الموديل',
      },
    },
    loadingScreen: {
      default: {
        en: 'Preparing the canvas for your dream car. Just a few more seconds to go!',
        ar: 'لحظات ونبدأ رحلة تصميم سيارة أحلامك... باقي ثوانٍ قليلة!',
      },
      enterConfigurator: {
        en: 'Build Your Ride',
        ar: 'ابنِ سيارتك',
      },
      loading: {
        en: 'Loading',
        ar: 'جارٍ التحميل',
      },
      variantLoading: {
        en: 'Trim is loading',
        ar: 'نموذج جارٍ التحميل',
      },
      viewLoading: {
        en: 'Applying final touches',
        ar: 'تطبيق اللمسات النهائية',
      },
      disclaimer: {
        en: 'Disclaimer',
        ar: 'تنصل',
      },
      termsConditions: {
        en: 'Terms & Conditions',
        ar: 'الشروط والأحكام',
      },
      viewSpecDetails: {
        en: 'View Spec Details',
        ar: 'ععرض تفاصيل المواصفات',
      },
      disclaimerContent: {
        en: 'Prices are subject to change without prior notice and may vary based on location, availability, or other factors.',
        ar: 'الأسعار عرضة للتغيير دون إشعار مسبق وقد تختلف بناءً على الموقع والتوافر وعوامل أخرى.',
      },
      termsModal: {
        title: {
          en: 'Configurator Use Terms and Conditions',
          ar: 'شروط وأحكام استخدام المُكوِّن',
        },
        lead: {
          en: 'By using this vehicle configurator and submitting your build, you acknowledge and agree to the following:',
          ar: 'من خلال استخدام مُكوِّن تكوين السيارة هذا وإرسال تصميمك، فإنك تقر وتوافق على ما يلي:',
        },
        sections: {
          item1: {
            title: {
              en: 'Non-Binding Submission',
              ar: 'تقديم غير ملزم',
            },
            content: {
              en: 'Submitting your vehicle configuration does not constitute a formal vehicle order or purchase agreement. No payment or deposit is collected at this stage.',
              ar: 'إرسال مواصفات سيارتك لا يُعد طلبًا رسميًا للسيارة أو اتفاقية شراء. لا يتم تحصيل أي مبلغ أو دفعة مقدمة في هذه المرحلة.',
            },
          },
          item2: {
            title: {
              en: 'Preliminary Estimate',
              ar: 'تقدير أولي',
            },
            content: {
              en: 'All pricing displayed is Manufacturer’s Suggested Retail Price (MSRP)and is provided for informational purposes only. Final pricing, taxes, fees, and applicable offers will be confirmed by the authorized dealer.',
              ar: 'جميع الأسعار المعروضة هي سعر التجزئة المقترح من الشركة المصنعة، وهي لأغراض إعلامية فقط. سيتم تأكيد الأسعار النهائية والضرائب والرسوم والعروض المطبقة من قبل الوكيل المعتمد.',
            },
          },
          item3: {
            title: {
              en: 'Dealer Follow-Up',
              ar: 'متابعة التاجر',
            },
            content: {
              en: 'Your submitted configuration will be shared with a participating dealer, who will contact you to discuss your preferences, availability, and next steps.',
              ar: 'سيتم مشاركة التكوين الذي أرسلته مع أحد التجار المشاركين، والذي سيتصل بك لمناقشة تفضيلاتك وتوافرك والخطوات التالية.',
            },
          },
          item4: {
            title: {
              en: 'Availability and Fulfillment',
              ar: 'التوفر والوفاء',
            },
            content: {
              en: 'We will make every reasonable effort to fulfill your preferences; however, final vehicle and accessories selection is subject to dealer inventory and production availability. Configurations may be limited to in-stock or incoming vehicles.',
              ar: 'سنبذل قصارى جهدنا لتلبية تفضيلاتكم، إلا أن الاختيار النهائي للمركبات والملحقات يخضع لمخزون الوكيل وتوافر الإنتاج. قد تقتصر التكوينات على المركبات المتوفرة في المخزون أو الواردة.',
            },
          },
          item5: {
            title: {
              en: 'No Obligation',
              ar: 'لا يوجد التزام',
            },
            content: {
              en: 'You are under no obligation to purchase the configured vehicle, and submitting your configuration does not create a contractual relationship between you and the manufacturer or dealer.',
              ar: 'لا يوجد أي التزام عليك بشراء السيارة المخصصة، كما أن إرسال التكوين الخاص بك لا يؤدي إلى إنشاء علاقة تعاقدية بينك وبين الشركة المصنعة أو التاجر.',
            },
          },
          item6: {
            title: {
              en: 'Use of Personal Information and Consent',
              ar: 'استخدام المعلومات الشخصية والموافقة',
            },
            content: {
              en: 'By submitting your configuration, you consent to the collection, use, and sharing of your personal information with authorized dealers to facilitate your request, in accordance with our [Privacy Statement].',
              ar: 'من خلال إرسال التكوين الخاص بك، فإنك توافق على جمع معلوماتك الشخصية واستخدامها ومشاركتها مع التجار المعتمدين لتسهيل طلبك، وفقًا لـ [بيان الخصوصية] الخاص بنا.',
            },
          },
          item7: {
            title: {
              en: 'System Limitations',
              ar: 'قيود النظام',
            },
            content: {
              en: 'While we aim to provide accurate and current information, the configurator may not reflect all available features or up-to-date pricing. Errors or omissions may occur.',
              ar: 'مع أننا نهدف إلى توفير معلومات دقيقة وحديثة، إلا أن مُكوِّن الإعدادات قد لا يعكس جميع الميزات المتاحة أو الأسعار المُحدَّثة. قد تحدث أخطاء أو سهو.',
            },
          },
        },
      },
    },
    common: {
      exit: {
        en: 'Exit',
        ar: 'خروج',
      },
      close: {
        en: 'Close',
        ar: 'إغلاق',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      back: {
        en: 'Back',
        ar: 'خلف',
      },
      next: {
        en: 'Next',
        ar: 'التالي',
      },
      done: {
        en: 'Done',
        ar: 'تم',
      },
      exterior: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interior: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      enterFullscreen: {
        en: 'Enter Fullscreen',
        ar: 'ملء الشاشة',
      },
      exitFullscreen: {
        en: 'Exit Fullscreen',
        ar: 'إلغاء ملء الشاشة',
      },
      selectTrim: {
        en: 'Select Trim',
        ar: 'اختر الفئة',
      },
      viewConfiguration: {
        en: 'View Configuration',
        ar: 'عرض التكوين',
      },
      togglePanelTooltip: {
        en: 'Select Trim and Configuration',
        ar: 'اختر الفئة والتكوين',
      },
      nextStepTooltip: {
        en: 'Click to proceed to the next step',
        ar: 'انقر للانتقال إلى الخطوة التالية',
      },
      heading: {
        en: 'Standard Features',
        ar: 'المميزات  القياسية',
      },
      description: {
        en: 'Chevrolet Arabia',
        ar: 'شيفروليه العربية',
      },
      goBack: {
        en: 'Go Back',
        ar: 'العودة',
      },
      freeRoamView: {
        en: 'Free Roam View',
        ar: 'عرض التجوال الحر',
      },
      featureView: {
        en: 'Featured View',
        ar: 'عرض مميز',
      },
      configurator: {
        en: 'Configurator',
        ar: 'المُكوِّن',
      },
      buildYourRide: {
        en: 'Build Your Ride',
        ar: 'ابنِ سيارتك',
      },
      options: {
        en: 'Options',
        ar: 'الخيارات',
      },
      summary: {
        en: 'Summary',
        ar: 'الملخص',
      },
      saveYourBuild: {
        en: 'Save Your Build',
        ar: 'احفظ تكوينك',
      },
      startingPrice: {
        en: 'Starting Price',
        ar: 'السعر الابتدائي',
      },
      msrp: {
        en: 'MSRP',
        ar: 'سعر التجزئة المقترح',
      },
      proceed: {
        en: 'Proceed',
        ar: 'متابعة',
      },
    },
    tabs: {
      config: {
        en: 'Config',
        ar: 'الإعداد',
      },
      model: {
        en: 'Model',
        ar: 'الموديل',
      },
      exterior: {
        en: 'Exterior',
        ar: 'الخارجي',
      },
      interior: {
        en: 'Interior',
        ar: 'الداخلي',
      },
      options: {
        en: 'Options',
        ar: 'الخيارات',
      },
      summary: {
        en: 'Summary',
        ar: 'الملخص',
      },
    },
    cart: {
      title: {
        en: 'Your Order',
        ar: 'طلبك',
      },
      disclaimer: {
        en: 'Accessory prices and availability will be confirmed at the showroom.',
        ar: 'سيتم تأكيد أسعار الملحقات وتوافرها في صالة العرض.',
      },
      exteriorLabel: {
        en: 'Exterior:',
        ar: ':خارجي',
      },
      interiorLabel: {
        en: 'Interior:',
        ar: ':داخلي',
      },
      addedAccessories: {
        en: 'Added Accessories',
        ar: 'الإكسسوارات المضافة',
      },
      priceEstimate: {
        en: 'Price Estimate',
        ar: 'السعر التقديري',
      },
      priceBreakdown: {
        en: 'Price Breakdown',
        ar: 'تفاصيل السعر',
      },
      reserveNow: {
        en: 'Proceed',
        ar: 'متابعة',
      },
      removeAccessory: {
        en: 'Remove accessory',
        ar: 'إزالة الإكسسوار',
      },
      removeItem: {
        en: 'Remove',
        ar: 'إزالة',
      },
      packageCategory: {
        en: 'Package',
        ar: 'حزمة',
      },
    },
    saveDialog: {
      optionsTitle: {
        en: 'How would you like to proceed?',
        ar: 'كيف تريد المتابعة؟',
      },
      optionWhatsapp: {
        en: 'Save to WhatsApp',
        ar: 'حفظ على واتساب',
      },
      optionEmail: {
        en: 'Save to Email',
        ar: 'حفظ على البريد الإلكتروني',
      },
      optionTestDrive: {
        en: 'Book a Test Drive',
        ar: 'احجز اختبار قيادة',
      },
      optionCallback: {
        en: 'Request a call Back',
        ar: 'طلب معاودة الاتصال',
      },
      labelFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      labelLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      labelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      labelMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      labelPrefix: {
        en: 'Prefix',
        ar: 'البادئة',
      },
      labelModel: {
        en: 'Model',
        ar: 'نموذج',
      },
      labelDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      labelComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      labelTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      labelTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      placeholderTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      placeholderLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      placeholderEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      placeholderMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      placeholderComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      placeholderSelectDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      privacyTitle: {
        en: 'Privacy statement & legal disclaimer',
        ar: 'بيان الخصوصية وإخلاء المسؤولية القانونية',
      },
      privacyText: {
        en: 'I acknowledge and understand that my information will be shared with General Motors, its affiliates, dealer network or other parties are required by law for compliance, safety campaigns, government inquiries, or similar legal process. My information may also be shared for product research and development purposes, and to manage customer relationships to provide support. GM’s Privacy Statement addresses how GM handles the personal information shared with us.',
        ar: 'أقر وأتفهم بأنة سيتم مشاركة معلوماتي مع شركة جنرال موتورز، والشركات التابعة لها، وشبكة الوكلاء أو الأطراف الأخرى بموجب القانون للامتثال وحملات السلامة واستفسارات الحكومية وأي إجراء قانوني مماثل. وقد تتم مشاركة معلوماتي أيضًا لأغراض البحث وتطوير المنتجات ، وإدارة علاقة العملاء لتوفير الدعم اللازم. يوضح بيان خصوصية جنرال موتورز على موقعنا كيفية تعامل جنرال موتورز مع المعلومات الشخصية التي تمت مشاركتها معنا.',
      },
      privacyReadMore: {
        en: 'Read more',
        ar: 'اقرأ المزيد',
      },
      privacyReadMoreUrl: {
        en: 'https://www.gmcarabia.com/ae-en/privacy',
        ar: 'https://www.gmcarabia.com/ae-ar/privacy',
      },
      privacyMarketingConsent: {
        en: 'I wish to receive any marketing information or have my information shared with third parties for purposes of providing me with marketing information.',
        ar: 'أرغب في تلقي معلومات تسويقية أو مشاركة معلوماتي مع جهات خارجية لغاية تزويدي بمعلومات تسويقية',
      },
      buttonSend: {
        en: 'Send Configuration',
        ar: 'إرسال التكوين',
      },
      buttonSending: {
        en: 'Submitting...',
        ar: 'تقديم...',
      },
      buttonSent: {
        en: 'Sent!',
        ar: 'مرسل!',
      },
      buttonRetry: {
        en: 'Retry',
        ar: 'إعادة المحاولة',
      },
      buttonClose: {
        en: 'Okay, Got it',
        ar: 'حسنًا، فهمت',
      },
      successTitle: {
        en: 'Saved',
        ar: 'محفوظ',
      },
      successLabelModel: {
        en: 'Model Name',
        ar: 'اسم النموذج',
      },
      successLabelVariant: {
        en: 'Variant Name',
        ar: 'اسم البديل',
      },
      successLabelName: {
        en: 'Name',
        ar: 'اسم',
      },
      successLabelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      successLabelMobile: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      qrTitle: {
        en: 'Your Unique Configuration QR',
        ar: 'رمز الاستجابة السريعة لتكوينك',
      },
      qrDescription: {
        en: 'Show this QR code to your nearest dealer to access your saved configuration.',
        ar: 'أظهر رمز الاستجابة السريعة هذا لتاجر التجزئة الأقرب للوصول إلى التكوين المحفوظ.',
      },
      qrNoteLabel: {
        en: 'Note:',
        ar: 'ملاحظة:',
      },
      qrNote: {
        en: 'Only authorized dealers can view this configuration.',
        ar: 'يمكن للوكلاء المعتمدين فقط عرض هذا التكوين.',
      },
      qrDownload: {
        en: 'Download QR',
        ar: 'تحميل QR',
      },
      qrSave: {
        en: 'Save QR',
        ar: 'حفظ QR',
      },
      errorTitle: {
        en: 'Submission Failed',
        ar: 'فشل التقديم',
      },
      errorMessage: {
        en: 'We encountered an error, please try again.',
        ar: 'واجهنا خطأ، يرجى المحاولة مرة أخرى.',
      },
    },
    featureGroups: {
      exteriorDesign: {
        en: 'Exterior Design & Styling',
        ar: 'التصميم الخارجي والتصميم',
      },
      interiorComfort: {
        en: 'Interior Comfort & Features',
        ar: 'الراحة والميزات الداخلية',
      },
      technologyConvenience: {
        en: 'Technology & Convenience',
        ar: 'التكنولوجيا والراحة',
      },
      safetyAssistance: {
        en: 'Safety & Driver Assistance',
        ar: 'السلامة ومساعدة السائق',
      },
    },
    sidePanel: {
      menu: {
        en: 'Main Menu',
        ar: 'القائمة الرئيسية',
      },
      search: {
        en: 'Search',
        ar: 'بحث',
      },
      settings: {
        en: 'Settings',
        ar: 'الإعدادات',
      },
      profile: {
        en: 'Profile',
        ar: 'الملف الشخصي',
      },
      notifications: {
        en: 'Notifications',
        ar: 'الإشعارات',
      },
      help: {
        en: 'Help & Support',
        ar: 'المساعدة والدعم',
      },
      filter: {
        en: 'Filter Options',
        ar: 'خيارات التصفية',
      },
      favorites: {
        en: 'Favorites',
        ar: 'المفضلة',
      },
      history: {
        en: 'History',
        ar: 'التاريخ',
      },
      tools: {
        en: 'Tools',
        ar: 'الأدوات',
      },
      variants: {
        en: 'Variants',
        ar: 'الأنواع',
      },
      exteriorColor: {
        en: 'Exterior Color',
        ar: 'لون الهيكل',
      },
      interiorColor: {
        en: 'Interior Color',
        ar: 'ألوان المقصورة',
      },
      features: {
        en: 'Features',
        ar: 'الميزات',
      },
      step: {
        en: 'Step',
        ar: 'خطوة',
      },
      openPanel: {
        en: 'Open Panel',
        ar: 'فتح اللوحة',
      },
      closePanel: {
        en: 'Close Panel',
        ar: 'إغلاق اللوحة',
      },
    },
    landscape: {
      message: {
        en: 'Please rotate your device to portrait mode',
        ar: 'يرجى تدوير جهازك إلى الوضع الرأسي',
      },
      subtitle: {
        en: 'For the best experience, please turn your device upright.',
        ar: 'للحصول على أفضل تجربة، يرجى إمساك جهازك بشكل رأسي.',
      },
    },
    cameraViewToggle: {
      ext: {
        en: 'Ext',
        ar: 'خارجي',
      },
      int: {
        en: 'Int',
        ar: 'داخلي',
      },
      dualTone: {
        en: 'Dual-tone',
        ar: 'لون مزدوج',
      },
    },
    bottomDrawer: {
      variants: {
        en: 'Variants',
        ar: 'الأنواع',
      },
      interactions: {
        en: 'Interactions',
        ar: 'التفاعلات',
      },
      accessories: {
        en: 'Options & Accessories',
        ar: 'الإكسسوارات',
      },
      features: {
        en: 'Features',
        ar: 'الميزات',
      },
      highlights: {
        en: 'Highlights',
        ar: 'الميزات البارزة',
      },
      moreOptions: {
        en: 'More Options',
        ar: 'المزيد من الخيارات',
      },
      colorSelector: {
        en: 'Color Selector',
        ar: 'محدد اللون',
      },
      exteriorColor: {
        en: 'Exterior Color',
        ar: 'لون الهيكل',
      },
      interiorColor: {
        en: 'Interior Color',
        ar: 'لون المقصورة',
      },
      wheels: {
        en: 'Wheels',
        ar: 'العجلات',
      },
      roofs: {
        en: 'Roofs',
        ar: 'الأسقف',
      },
      performance: {
        en: 'Performance',
        ar: 'الأداء',
      },
      packages: {
        en: 'Packages',
        ar: 'الحزم',
      },
      chargingOptions: {
        en: 'Charging Options',
        ar: 'خيارات الشحن',
      },
      safetyServices: {
        en: 'Safety & Services',
        ar: 'السلامة والخدمات',
      },
      expand: {
        en: 'Expand',
        ar: 'توسيع',
      },
      minimize: {
        en: 'Minimize',
        ar: 'تصغير',
      },
      freeRoamView: {
        en: 'Free Roam View',
        ar: 'عرض التجوال الحر',
      },
      featureView: {
        en: 'Featured View',
        ar: 'العرض المميز',
      },
    },
    qr: {
      scan: {
        en: 'Scan QR',
        ar: 'امسح رمز الاستجابة السريعة',
      },
      scanWithPhone: {
        en: 'Scan with your phone to view in your Space',
        ar: 'امسح باستخدام هاتفك لعرضه في مساحتك',
      },
    },
    share: {
      title: {
        en: 'Share Configuration',
        ar: 'مشاركة التكوين',
      },
      description: {
        en: 'Share your vehicle configuration with others',
        ar: 'شارك تكوين سيارتك مع الآخرين',
      },
      twitterTitle: {
        en: 'Check out my GMC Acadia configuration!',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس!',
      },
      whatsappTitle: {
        en: 'Check out my GMC Acadia configuration!',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس!',
      },
      linkedinTitle: {
        en: 'My GMC Acadia Configuration',
        ar: 'تكويني لسيارة شيفروليه ترافيرس',
      },
      linkedinSummary: {
        en: 'Check out my custom GMC Acadia configuration!',
        ar: 'شاهد تكويني المخصص لسيارة شيفروليه ترافيرس!',
      },
      emailSubject: {
        en: 'Check out my GMC Acadia Configuration',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس',
      },
      emailBody: {
        en: 'I configured my dream GMC Acadia. Take a look:',
        ar: 'قمت بتكوين سيارة شيفروليه ترافيرس المثالية. ألق نظرة:',
      },
      linkLabel: {
        en: 'Or copy link:',
        ar: 'أو انسخ الرابط:',
      },
      copied: {
        en: 'Copied',
        ar: 'تم نسخ الرابط إلى الحافظة!',
      },
      copy: {
        en: 'Copy',
        ar: 'نسخ',
      },
      saveAndShare: {
        en: 'Save & Share',
        ar: 'حفظ ومشاركة',
      },
      copyLink: {
        en: 'Copy link',
        ar: 'نسخ الرابط',
      },
      facebook: {
        en: 'Facebook',
        ar: 'Facebook',
      },
      twitter: {
        en: 'Twitter',
        ar: 'Twitter',
      },
      gmail: {
        en: 'Gmail',
        ar: 'Gmail',
      },
      whatsapp: {
        en: 'Whatsapp',
        ar: 'Whatsapp',
      },
    },
    topBar: {
      matchingVehiclesFound: {
        en: 'Matching Vehicles Found',
        ar: 'تم العثور على المركبات المطابقة',
      },
    },
    bottomBar: {
      priceLabel: {
        en: 'ex showroom price',
        ar: 'سعر صالة العرض',
      },
      leads: {
        en: 'Save',
        ar: 'حفظ',
      },
      findLocalMatches: {
        en: 'Find Local Matches',
        ar: 'اعثر على التطابقات المحلية',
      },
      save: {
        en: 'Save',
        ar: 'حفظ',
      },
      item: {
        en: 'Item',
        ar: 'العنصر',
      },
      items: {
        en: 'Items',
        ar: 'عناصر',
      },
      totalToPay: {
        en: 'Total to pay',
        ar: 'الإجمالي للدفع',
      },
      bookTestDrive: {
        en: 'Save',
        ar: 'حفظ',
      },
      requestQuote: {
        en: 'Request a Quote',
        ar: 'طلب عرض سعر',
      },
      downloadBrochure: {
        en: 'Download Brochure',
        ar: 'تحميل الكتيب',
      },
    },
    priceSummary: {
      title: {
        en: 'Price Summary',
        ar: 'ملخص السعر',
      },
      enterLocation: {
        en: 'Enter Location',
        ar: 'أدخل الموقع',
      },
      selectCountry: {
        en: 'Location',
        ar: 'الموقع',
      },
      selectCity: {
        en: 'Location',
        ar: 'الموقع',
      },
      note: {
        en: 'Note : Prices might vary by state and all prices listed are on road price, T&C apply.',
        ar: 'ملاحظة: قد تختلف الأسعار حسب الولاية وجميع الأسعار المذكورة هي الأسعار على الطريق، تطبق الشروط والأحكام.',
      },
      breakdown: {
        en: 'Price Breakdown',
        ar: 'تفصيل السعر',
      },
      color: {
        en: 'Color',
        ar: 'اللون',
      },
      variant: {
        en: 'Variant',
        ar: 'الفئة',
      },
      included: {
        en: 'Included',
        ar: 'مشمول',
      },
      total: {
        en: 'Total',
        ar: 'الإجمالي',
      },
    },
    featureDialog: {
      playPause: {
        en: 'Play-Pause',
        ar: 'تشغيل-إيقاف',
      },
    },
    selectDealership: {
      title: {
        en: 'Select a Dealer',
        ar: 'اختر وكيلاً',
      },
      subtitle: {
        en: 'Please select a dealer to find local matches similar to your configuration',
        ar: 'يرجى اختيار وكيل للعثور على سيارات مطابقة لتكوينك في منطقتك',
      },
      city: {
        en: 'UAE',
        ar: 'الإمارات',
      },
      dealersIn: {
        en: 'Dealers in',
        ar: 'وكلاء في',
      },
      dealersInCity: {
        en: 'Dealers in {{city}}',
        ar: 'وكلاء في {{city}}',
      },
      availableInLocations: {
        en: 'Available in {{count}} locations of {{city}}',
        ar: 'متوفرة في {{count}} مواقع في {{city}}',
      },
      proceed: {
        en: 'Select Dealer and Proceed to Configurator',
        ar: 'اختر الوكيل وتابع إلى المتكون',
      },
    },
    countryNames: {
      UAE: {
        en: 'UAE',
        ar: 'الإمارات',
      },
      KSA: {
        en: 'KSA',
        ar: 'السعودية',
      },
      KW: {
        en: 'Kuwait',
        ar: 'الكويت',
      },
      default: {
        en: 'UAE',
        ar: 'الإمارات',
      },
    },
    localMatches: {
      exterior: {
        en: 'Exterior',
        ar: 'خارجي',
      },
      interior: {
        en: 'Interior',
        ar: 'داخلي',
      },
      dualTone: {
        en: 'Dual-tone',
        ar: 'لون مزدوج',
      },
      yourConfiguration: {
        en: 'Current Configuration',
        ar: 'التكوين الحالي',
      },
      viewConfiguration: {
        en: 'View your configuration details',
        ar: 'عرض تفاصيل تكوينك',
      },
      yourBuildDetails: {
        en: 'Your Build Details',
        ar: 'تفاصيل بنائك',
      },
      options: {
        en: 'Options',
        ar: 'الخيارات',
      },
      save: {
        en: 'Save',
        ar: 'حفظ',
      },
      dealersIn: {
        en: 'Dealers in',
        ar: 'وكلاء في',
      },
      changeDealer: {
        en: 'Change Dealer',
        ar: 'تغيير الوكيل',
      },
      carsAvailable: {
        en: 'Cars Available at the Selected Dealership',
        ar: 'السيارات المتوفرة لدى الوكيل المختار',
      },
      matchesFound: {
        en: 'Matches found',
        ar: 'تطابق تم العثور عليه',
      },
      startingFrom: {
        en: 'Starting from',
        ar: 'يبدأ من',
      },
      getQuote: {
        en: 'Proceed',
        ar: 'احصل على عرض سعر',
      },
      viewDifferences: {
        en: 'View Differences',
        ar: 'عرض الاختلافات',
      },
      match: {
        en: 'Match',
        ar: 'تطابق',
      },
      closestMatchFound: {
        en: 'Closest Match Found',
        ar: 'أقرب تطابق تم العثور عليه',
      },
      buildDetails: {
        en: 'Build Details',
        ar: 'تفاصيل التكوين',
      },
      trim: {
        en: 'Trim',
        ar: 'الفئة',
      },
      matchScoreTooltip: {
        en: 'This score indicates how closely this vehicle matches your configuration.',
        ar: 'يشير هذا النتيجة إلى مدى تطابق هذه السيارة مع تكوينك.',
      },
      exactMatch: {
        en: 'Exact Match',
        ar: 'تطابق تام',
      },
      similarMatch: {
        en: 'Close Match',
        ar: 'تطابق قريب',
      },
      availableNow: {
        en: 'Available Now',
        ar: 'متوفر الآن',
      },
      whatYouConfigured: {
        en: 'What you configured',
        ar: 'ما قمت بتكوينه',
      },
      matchingInStock: {
        en: 'Matching vehicle available in stock',
        ar: 'مركبة مطابقة متوفرة في المخزون',
      },
      noMatches: {
        en: 'No matching vehicles found in your area.',
        ar: 'لم يتم العثور على سيارات مطابقة في منطقتك.',
      },
      closeMatchDetails: {
        en: 'Close Match Details',
        ar: 'تفاصيل التطابق القريب',
      },
      matchesYourSelection: {
        en: 'Matches your selection',
        ar: 'يطابق اختيارك',
      },
      sameAsWhatYouSelected: {
        en: 'Same as what you selected',
        ar: 'نفس ما اخترته',
      },
      replacesYourSelection: {
        en: 'Replaces your selection',
        ar: 'يستبدل اختيارك',
      },
      selectionNotIncluded: {
        en: 'Selection not included',
        ar: 'الاختيار غير مشمول',
      },
      includedInThisVehicle: {
        en: 'Included in this vehicle',
        ar: 'مشمول في هذه المركبة',
      },
      additionalFeatureAlreadyIncluded: {
        en: 'Additional feature already included',
        ar: 'ميزة إضافية مشمولة بالفعل',
      },
      stockDisclaimer: {
        en: 'Available stock may include extra features not selected in your original configuration.',
        ar: 'قد تشمل المركبات المتاحة ميزات إضافية لم تختارها في تكوينك الأصلي.',
      },
      additionalInstalledOptionsLegend: {
        en: 'Included in this vehicle',
        ar: 'مشمول في هذه المركبة',
      },
      vehicleDetails: {
        en: 'Vehicle Details',
        ar: 'تفاصيل المركبة',
      },
      installedOptionsTitle: {
        en: 'Comes with vehicle',
        ar: 'يأتي مع المركبة',
      },
      additionalInstalledOptionsSubtitle: {
        en: 'Additional Installed Options:',
        ar: 'الخيارات المركبة الإضافية:',
      },
      specTrim: {
        en: 'Trim',
        ar: 'الفئة',
      },
      specExteriorColor: {
        en: 'Exterior Color',
        ar: 'اللون الخارجي',
      },
      specInteriorColor: {
        en: 'Interior Color',
        ar: 'اللون الداخلي',
      },
      specWheels: {
        en: 'Wheels',
        ar: 'العجلات',
      },
      specPackage: {
        en: 'Package',
        ar: 'الحزمة',
      },
      title: {
        en: 'Configurations Found',
        ar: 'تكوينات تم العثور عليها',
      },
      message: {
        en: 'We Found a few local matches similar to your current configuration. Click to view Matches',
        ar: 'وجدنا بعض التطابقات المحلية المشابهة لتكوينك الحالي. انقر لعرض التطابقات',
      },
    },
    trim: {
      selection: {
        en: 'Trim Selection',
        ar: 'اختيار الفئة',
      },
    },
    paymentPage: {
      exteriorColor: {
        en: 'Exterior Color',
        ar: 'لون الهيكل',
      },
      interiorColor: {
        en: 'Interior Color',
        ar: 'لون المقصورة',
      },
      accessory: {
        en: 'Accessory',
        ar: 'ملحق',
      },
      closePayment: {
        en: 'Close',
        ar: 'إغلاق',
      },
    },
    packageModal: {
      includes: {
        en: 'Includes:',
        ar: 'يشمل:',
      },
      of: {
        en: 'of',
        ar: 'من',
      },
      totalPriceChange: {
        en: 'Total Price Change:',
        ar: 'إجمالي تغيير السعر:',
      },
      item: {
        en: 'Item',
        ar: 'غرض',
      },
      items: {
        en: 'Items',
        ar: 'أغراض',
      },
      addPackage: {
        en: 'Add Package',
        ar: 'إضافة الحزمة',
      },
      noThanks: {
        en: 'No Thanks',
        ar: 'لا شكراً',
      },
      alreadyApplied: {
        en: 'Already Applied',
        ar: 'مُطبّق بالفعل',
      },
    },
    changesRequired: {
      en: 'Changes Required',
      ar: 'التغييرات المطلوبة',
    },
    changesRequiredDescription: {
      en: 'The package you selected conflicts with your current selections. Please review the changes below.',
      ar: 'الحزمة التي اخترتها تتعارض مع اختياراتك الحالية. يرجى مراجعة التغييرات أدناه.',
    },
    itemsThatWillBeRemoved: {
      en: 'Items that will be removed',
      ar: 'العناصر التي سيتم إزالتها',
    },
    itemsThatWillBeAdded: {
      en: 'Items that will be added',
      ar: 'العناصر التي سيتم إضافتها',
    },
    applyChanges: {
      en: 'Apply Changes',
      ar: 'تطبيق التغييرات',
    },
    previewFeature: {
      en: 'Preview Feature',
      ar: 'معاينة الميزة',
    },
    exploreFeatures: {
      en: 'Explore Features',
      ar: 'استكشف الميزات',
    },
    buildAndPrice: {
      en: 'Build & Price',
      ar: 'البناء والسعر',
    },
    features: {
      preview: {
        en: 'Preview Feature',
        ar: 'معاينة المميزات',
      },
      playAll: {
        en: 'PLAY ALL',
        ar: 'تشغيل الكل',
      },
      title: {
        en: 'Features',
        ar: 'المميزات',
      },
    },
    header: {
      localMatches: {
        en: 'Local\nMatches',
        ar: 'التطابقات\nالمحلية',
      },
      matchingVehicles: {
        en: 'Matching vehicles',
        ar: 'مركبات مطابقة',
      },
      configurationsFound: {
        en: 'Configurations Found',
        ar: 'تكوينات تم العثور عليها',
      },
    },
    configPanel: {
      playAll: {
        en: 'Play All',
        ar: 'تشغيل الكل',
      },
      pause: {
        en: 'Pause',
        ar: 'إيقاف مؤقت',
      },
      exteriorColors: {
        en: 'Exterior Colors',
        ar: 'ألوان الهيكل الخارجي',
      },
      interiorColors: {
        en: 'Interior Colors',
        ar: 'ألوان الداخلية',
      },
      roofOptions: {
        en: 'Roof Options',
        ar: 'خيارات السقف',
      },
      interiorOptions: {
        en: 'Interior Options',
        ar: 'خيارات الداخلية',
      },
      exteriorAccessories: {
        en: 'Exterior Accessories',
        ar: 'إكسسوارات خارجية',
      },
      noAdditionalOptions: {
        en: 'No additional options available for the selected trim.',
        ar: 'لا توجد خيارات إضافية متاحة للفئة المختارة.',
      },
      noFilters: {
        en: 'No filters available.',
        ar: 'لا توجد فلاتر متاحة.',
      },
      noVariantsMatch: {
        en: 'No trims match the selected filters.',
        ar: 'لا توجد فئات تطابق الفلاتر المختارة.',
      },
      configurationBreakdown: {
        en: 'Configuration Breakdown',
        ar: 'تفاصيل التكوين',
      },
      yourConfiguration: {
        en: 'Your Configuration',
        ar: 'تكوينك',
      },
      trim: {
        en: 'Trim',
        ar: 'الفئة',
      },
      total: {
        en: 'Total',
        ar: 'الإجمالي',
      },
    },
    conflictDialog: {
      description: {
        en: 'In order to apply this, the following item(s) will need to be removed due to compatibility constraints. Please review the item(s) below and proceed with your change.',
        ar: 'لتطبيق هذا، يجب إزالة العناصر التالية بسبب قيود التوافق. يرجى مراجعة العناصر أدناه والمتابعة بتغييرك.',
      },
    },
    viewport: {
      changeBackground: {
        en: 'Change Background',
        ar: 'تغيير الخلفية',
      },
      selectLanguage: {
        en: 'Switch Language (عربي)',
        ar: 'تبديل اللغة (EN)',
      },
      menu: {
        en: 'Menu',
        ar: 'القائمة',
      },
      exploreFeatures: {
        en: 'Explore Features',
        ar: 'استكشف الميزات',
      },
    },
  },
  dealers: {
    UAE: [
      {
        id: 0,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: 'UAE',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: 'UAE',
        dealerId: '323482',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Madinat Zayed, Al Dhafra',
          ar: 'بن حمودة للسيارات - مدينة زايد، الظفرة',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122552',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Al Ain',
          ar: 'بن حمودة للسيارات - العين',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122233',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Abu Dhabi Main',
          ar: 'بن حمودة للسيارات - أبوظبي الرئيسية',
        },
      },
    ],
    KSA: [
      {
        id: 0,
        country: 'KSA',
        value: 'KSA',
        dealerId: '307261',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Aziziyah Madina Road, Jeddah',
          ar: 'الجميح للسيارات - العزيزية طريق المدينة، جدة',
        },
      },
      {
        id: 1,
        country: 'KSA',
        value: 'KSA',
        dealerId: '164034',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Hail City King Abdullah Road. Hail',
          ar: 'الجميح للسيارات - مدينة حائل طريق الملك عبدالله. يشيد',
        },
      },
      {
        id: 2,
        country: 'KSA',
        value: 'KSA',
        dealerId: '164035',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Alshlhoub King Abdullah Road, Al Jouf',
          ar: 'الجميح للسيارات - الشلهوب طريق الملك عبدالله، الجوف',
        },
      },
      {
        id: 3,
        country: 'KSA',
        value: 'KSA',
        dealerId: '307271',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Qassim King Abdulaziz Road, Buraidah',
          ar: 'الجميح للسيارات - القصيم طريق الملك عبد العزيز، بريدة',
        },
      },
      {
        id: 4,
        country: 'KSA',
        value: 'KSA',
        dealerId: '260921',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Jededah Road, Arar',
          ar: 'الجميح للسيارات - طريق الجديدة، عرعر',
        },
      },
    ],
    KW: [
      {
        id: 0,
        country: 'KW',
        value: 'KW',
        dealerId: '295892',
        dealerName: 'Mohammed Saleh & Reza Yousuf Behbehani Co',
        label: {
          en: 'Mohammed Saleh & Reza Yousuf Behbehani Co., Mohammed Bin Al Qassem Street, Al Rai',
          ar: 'شركة محمد صالح ورضا يوسف بهبهاني، شارع محمد بن القاسم، الري',
        },
      },
      {
        id: 1,
        country: 'KW',
        value: 'KW',
        dealerId: '122225',
        dealerName: 'Mohammed Saleh & Reza Yousuf Behbehani Co',
        label: {
          en: 'Mohammed Saleh & Reza Yousuf Behbehani Co., Mohammed Bin Al Qassem Street, Al Rai, Abdul Hameed Salem Sons Co, Al Rai',
          ar: 'شركة محمد صالح ورضا يوسف بهبهاني, شارع محمد بن القاسم, الري, شركة أبناء عبد الحميد سالم, الري',
        },
      },
      {
        id: 2,
        country: 'KW',
        value: 'KW',
        dealerId: '260852',
        dealerName: 'Mohammed Saleh & Reza Yousuf Behbehani Co',
        label: {
          en: 'Mohammed Saleh & Reza Yousuf Behbehani Co., Omar Ibn Al Khatab Street, Sharq',
          ar: 'شركة محمد صالح ورضا يوسف بهبهاني، شارع عمر بن الخطاب، شرق',
        },
      },
    ],
    BHD: [
      {
        id: 0,
        country: 'BHD',
        value: 'BHD',
        dealerId: '122215',
        dealerName: 'National Motor Company W.L.L',
        label: {
          en: 'National Motor Company W.L.L, Al Qarya 475 Shk. Jaber Al Subah Hwy',
          ar: 'الشركة الوطنية للسيارات ذ.م.م، القرية 475 شارع الشيخ. طريق جابر الصباح',
        },
      },
    ],
    IQD: [
      {
        id: 0,
        country: 'IQD',
        value: 'IQD',
        dealerId: '295876',
        dealerName: 'MAC International FZE',
        label: {
          en: 'MAC International FZE, Al-Karada Sadoun Street',
          ar: 'شركة ماك الدولية (منطقة حرة)، شارع الكرادة سعدون',
        },
      },
      {
        id: 1,
        country: 'IQD',
        value: 'IQD',
        dealerId: '295879',
        dealerName: 'MAC International FZE',
        label: {
          en: 'MAC International FZE, Kurdistan District 100 m Street',
          ar: 'ماك إنترناشونال إف زي إي، منطقة كردستان، شارع ١٠٠ متر',
        },
      },
    ],
    QAR: [
      {
        id: 0,
        country: 'QAR',
        value: 'QAR',
        dealerId: '122234',
        dealerName: 'Mannai Trading Company',
        label: {
          en: 'Mannai Trading Company W.L.L., Salwa E Industrial Street',
          ar: 'شركة مناعي التجارية ذ.م.م، شارع سلوى إي الصناعي',
        },
      },
    ],
    OMR: [
      {
        id: 0,
        country: 'OMR',
        value: 'OMR',
        dealerId: '295854',
        dealerName: 'Moosa',
        label: {
          en: 'Moosa Abdul Rahman Hassan & Co. LLC, Al Nahda Main Road',
          ar: 'موسى عبد الرحمن حسن وشركاه ش.م.م، طريق النهضة الرئيسي',
        },
      },
      {
        id: 1,
        country: 'OMR',
        value: 'OMR',
        dealerId: '260877',
        dealerName: 'Moosa',
        label: {
          en: 'Moosa Abdul Rahman Hassan & Co. LLC, Al Firq Road',
          ar: 'موسى عبد الرحمن حسن وشركاه ش.م.م، طريق الفرق',
        },
      },
      {
        id: 2,
        country: 'OMR',
        value: 'OMR',
        dealerId: '260880',
        dealerName: 'Moosa',
        label: {
          en: 'Moosa Abdul Rahman Hassan & Co. LLC, New Industrial Road',
          ar: 'موسى عبد الرحمن حسن وشركاه ش.م.م، طريق الصناعية الجديدة',
        },
      },
    ],
    JOD: [
      {
        id: 0,
        country: 'JOD',
        value: 'JOD',
        dealerId: '189129',
        dealerName: 'Leading Vehicles',
        label: {
          en: 'The Leading Vehicles Co. Ltd, Al Rawabi',
          ar: 'شركة المركبات الرائدة المحدودة، الروابي',
        },
      },
    ],
    LBP: [
      {
        id: 0,
        country: 'LBP',
        value: 'LBP',
        dealerId: '122227',
        dealerName: 'RYMCO',
        label: {
          en: 'Rasamny Younis Motor Co. SAL, Michael Zakhour Road',
          ar: 'شركة رسامني يونس للسيارات (ش.م.ل)، طريق مايكل زخور',
        },
      },
    ],
    default: [
      {
        id: 0,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: 'UAE',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: 'UAE',
        dealerId: '323482',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Madinat Zayed, Al Dhafra',
          ar: 'بن حمودة للسيارات - مدينة زايد، الظفرة',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122552',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Al Ain',
          ar: 'بن حمودة للسيارات - العين',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122233',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Abu Dhabi Main',
          ar: 'بن حمودة للسيارات - أبوظبي الرئيسية',
        },
      },
    ],
  },
  featureFlags: {
    showPriceInPresentation: false,
    showLogo: true,
    showSpecDetailsButton: true,
    useChevroletFont: false,
    showLocalMatches: true,
    showDisclaimerInPresentation: false,
    // Per-item pricing (card / MODEL / SUMMARY price lines, colours in the total)
    // is opt-in; this project keeps the header-only pricing it has always had.
    showPrices: true, // per-item prices on cards + summary breakdown, like Corvette
    showDisplayCarPrice: true,
    showCart: false,
  },
  config: {
    // Display currency + USD conversion factor per region, keyed by the
    // `country` URL parameter (local = usd * fx). Prices were re-derived
    // from the AED figure: usd = AED / 3.68, then usd * fx per country.
    regions: {
      KSA: { label: 'KSA', currency: 'SAR', fx: 3.75 },
      UAE: { label: 'UAE', currency: 'AED', fx: 3.68 },
      OMR: { label: 'Oman', currency: 'OMR', fx: 0.39 },
      BHD: { label: 'Bahrain', currency: 'BHD', fx: 0.38 },
      QAR: { label: 'Qatar', currency: 'QAR', fx: 3.64 },
      JOD: { label: 'Jordan', currency: 'JOD', fx: 0.71 },
      KW: { label: 'Kuwait', currency: 'KWD', fx: 0.31 },
      LBP: { label: 'Lebanon', currency: 'USD', fx: 1 }, // GM quotes Lebanon in USD
      IQD: { label: 'Iraq', currency: 'USD', fx: 1 }, // priced in USD, like Lebanon
      default: { label: 'UAE', currency: 'AED', fx: 3.68 },
    },
    // Lead-side model name — 1.0's `config.model`, which is a lead label
    // distinct from 2.0's `config.model` (the models[] key).
    leadModel: 'acadia',
    // Dial code (prefixed onto mobile_number) and Salesforce lead country
    // code (metadome_country_code), keyed by the `country` URL parameter.
    countryCodes: {
      UAE: {
        code: '+971',
        leadCode: 'AE',
      },
      KSA: {
        code: '+966',
        leadCode: 'SA',
      },
      KW: {
        code: '+965',
        leadCode: 'KW',
      },
      BHD: {
        code: '+973',
        leadCode: 'BH',
      },
      IQD: {
        code: '+964',
        leadCode: 'IQ',
      },
      LBP: {
        code: '+961',
        leadCode: 'LB',
      },
      OMR: {
        code: '+968',
        leadCode: 'OM',
      },
      QAR: {
        code: '+974',
        leadCode: 'QA',
      },
      JOD: {
        code: '+962',
        leadCode: 'JO',
      },
      default: {
        code: '+971',
        leadCode: 'AE',
      },
    },
    model: 'Acadia',
    modelName: 'Acadia',
    pageTitle: 'Acadia Configurator',
    client: 'GM',
    brand: 'gmc',
    modelId: 'GCACADIA_MCM',
    configurationId: 'l25zrwex1u93',
    externalAnalytics: {
      provider: 'adobe',
      brand: 'gmc',
      modelName: 'acadia',
      vehicleType: 'suv',
    },
    presentation: {
      video: 'videos/2377/presentation_video_1.webm',
      loadingImage: 'images/2377/sliderImage.webp',
      transitionImage: 'images/2377/transition_logo.png',
      headerLogo: 'images/2377/transition_logo.png',
      headerLogoKeepColor: true,
      keyHighlights: [
        {
          index: 0,
          show: true,
          title: {
            en: '',
            ar: '',
          },
          value: {
            en: '328 HP ',
            ar: '328 حصاناً',
          },
          unit: '',
        },
        {
          index: 1,
          show: true,
          title: {
            en: 'Torque',
            ar: 'عزم الدوران',
          },
          value: {
            en: '442 NM',
            ar: '442 نيوتن متر',
          },
          unit: '',
        },
        {
          index: 2,
          show: true,
          title: {
            en: 'Turbo Engine',
            ar: 'محرك توربو',
          },
          value: {
            en: '2.5L',
            ar: '2.5 لتر',
          },
          unit: '',
        },
      ],
    },
  },
  saveFormConfig: {
    fields: [
      {
        id: 'firstName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelFirstName',
        placeholderKey: 'saveDialog.placeholderFirstName',
      },
      {
        id: 'lastName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelLastName',
        placeholderKey: 'saveDialog.placeholderLastName',
      },
      {
        id: 'email',
        type: 'email',
        required: true,
        labelKey: 'saveDialog.labelEmail',
        placeholderKey: 'saveDialog.placeholderEmail',
        visibility: [
          'email',
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'phone',
        type: 'phone',
        required: true,
        labelKey: 'saveDialog.labelMobileNumber',
        placeholderKey: 'saveDialog.placeholderMobileNumber',
        visibility: [
          'whatsapp',
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'model',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelModel',
        placeholderKey: 'saveDialog.labelModel',
        optionsSource: 'models',
        visibility: [
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'dealer',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelDealership',
        placeholderKey: 'saveDialog.placeholderSelectDealership',
        optionsSource: 'dealers',
        visibility: [
          'test-drive',
          'callback',
          'whatsapp',
          'email',
        ],
        // 1.0's save forms show the dealership dropdown but do not gate
        // submission on it; the booking forms and Save to WhatsApp
        // require it (GM needs a dealer on WhatsApp leads); Email stays optional.
        requiredFor: [
          'test-drive',
          'callback',
          'whatsapp',
        ],
      },
      {
        id: 'testDriveDate',
        type: 'date',
        required: true,
        labelKey: 'saveDialog.labelTestDriveDate',
        placeholderKey: 'saveDialog.placeholderTestDriveDate',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'testDriveTime',
        type: 'time',
        required: true,
        labelKey: 'saveDialog.labelTestDriveTime',
        placeholderKey: 'saveDialog.placeholderTestDriveTime',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'comments',
        type: 'textarea',
        required: false,
        labelKey: 'saveDialog.labelComments',
        placeholderKey: 'saveDialog.placeholderComments',
        visibility: [
          'test-drive',
          'callback',
        ],
      },
    ],
  },
  externalScript: {
    dev: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-bea0833819b4-development.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
    staging: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-77110a86d4ec-staging.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
    main: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-85b05c76e071.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
  },
  filterOptions: [],
  styleConfig: {
    cssVariables: {
      '--default-font-family': '\'Stratum GMC\', \'Chevy Sans\', sans-serif',
      '--bold-font-family': '\'Stratum GMC\', \'Chevy Sans Medium\', sans-serif',
      '--brand-primary': '#B9081B',
      '--brand-primary-hover': '#970716',
      '--brand-secondary': '#CC0000',
      '--brand-accent': '#B9081B',
      '--brand-accent-hover': '#970716',
      '--brand-accent-light': '#f7e4e9',
      '--brand-surface': '#f3f3f3',
      '--brand-surface-alt': '#f3f4f6',
      '--brand-text': '#000000',
      '--brand-text-secondary': '#666666',
      '--brand-text-tertiary': '#bebebe',
      '--brand-border': '#e6e6e6',
      '--brand-border-selected': '#B9081B',
      '--brand-card-bg': '#ffffff',
      '--brand-toggle-gradient-from': '#B9081B',
      '--brand-toggle-gradient-to': '#CC0000',
      '--brand-toggle-knob-border': '#B9081B',
      '--brand-accent-secondary': '#CC0000',
      '--brand-card-radius': '2px',
      '--brand-card-radius-sm': '2px',
      '--brand-card-radius-lg': '3px',
      '--brand-button-radius': '2px',
      '--brand-button-radius-sm': '2px',
      '--brand-pill-radius': '50px',
      '--brand-cta-radius': '2px',
      '--brand-button-border': 'var(--brand-accent)',
      '--brand-cta-arrow-bg': '#B9081B',
      '--brand-cta-arrow-fg': '#ffffff',
      '--brand-cta-shimmer': '#B9081B',
      '--brand-cta-loading-bg': '#B9081B',
    },
  },
} as unknown as UIMetadataComplete;
